import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router';
import axios from 'axios';

import { BASE_DOMAIN } from './settings';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(relativeTime);

import App from './App'
import HomePage from './components/top_level/HomePage'
import LoginPage from './components/top_level/LoginPage'
import AccountPage from './components/top_level/AccountPage'
import EmptyPage from './components/top_level/EmptyPage'
import TempPasswordChangePage from './components/top_level/TempPasswordChangePage'
import AddBaseStationPage from './components/top_level/AddBaseStationPage'
import BaseStationsPage from './components/top_level/BaseStationsPage'
import StrImportPage from './components/top_level/StrImportPage'
import AddBaselinePage from './components/top_level/AddBaselinePage'
import BaselinesPage from './components/top_level/BaselinesPage'
import RoversPage from './components/top_level/RoversPage'
import StatsPage from './components/top_level/StatsPage'
import EventsPage from './components/top_level/EventsPage'
import EventDetailPage from './components/events/EventDetailPage.vue'
import VerifyEmailPage from './components/account/VerifyEmailPage.vue'
import InfoPage from './components/top_level/InfoPage.vue'
import SettingsPage from './components/top_level/SettingsPage.vue'
import DashboardPage from './components/top_level/DashboardPage'
import InsightsPage from './components/top_level/InsightsPage'
import LicenseAlertPage from './components/top_level/LicenseAlertPage'
import CNetListPage from './components/top_level/CNetListPage'
import NotFoundPage from './components/top_level/NotFoundPage.vue'

import { useUserStore } from '@/stores/user';
import { useSessionStore } from '@/stores/session';
import i18n from '@/i18n';

import { applyUiTheme } from '@/api/uiThemes';
import './assets/global.less';

applyUiTheme(window.__UI_THEME__ ?? '');

const app = createApp(App);

const routes = [
	{
		path: '/',
		name: 'home-map',
		component: EmptyPage,
	},
	{
		// look for focusedMountpoint in code
		path: '/on-map/:focusedMountpoint',
		name: 'bs-on-map',
		component: EmptyPage,
	},
	{
		path: '/home',
		name: 'home-public',
		component: HomePage,
	},
	{
		path: '/login',
		name: 'login',
		component: LoginPage,
	},
	{
		path: '/account',
		name: 'account',
		component: AccountPage,
	},
	{
		path: '/temp-password-change',
		name: 'temp-password-change',
		component: TempPasswordChangePage,
	},
	{
		path: '/add-base-station',
		name: 'add-base-station',
		component: AddBaseStationPage,
	},
	{
		path: '/duplicate-base-station',
		name: 'duplicate-base-station',
		component: AddBaseStationPage,
	},
	{
		path: '/base-stations',
		name: 'base-stations',
		component: BaseStationsPage,
	},
	{
		path: '/base-stations/:id',
		name: 'base-stations-edit',
		component: AddBaseStationPage,
	},
	{
		path: '/str-import',
		name: 'str-import',
		component: StrImportPage,
	},
	{
		path: '/add-baseline',
		name: 'add-baseline',
		component: AddBaselinePage,
	},
	{
		path: '/baselines',
		name: 'baselines',
		component: BaselinesPage,
	},
	{
		path: '/baselines/:id',
		name: 'baselines-edit',
		component: AddBaselinePage,
	},
	{
		path: '/rovers',
		name: 'rovers',
		component: RoversPage,
	},
	{
		path: '/stats',
		name: 'stats',
		component: StatsPage,
	},
	{
		path: '/stats/:id/:subId?',
		name: 'stats-type',
		component: StatsPage,
	},
	{
		path: '/events',
		name: 'events',
		component: EventsPage,
	},
	{
		path: '/events/:id',
		name: 'events-detail',
		component: EventDetailPage,
	},
	{
		path: '/verify-email/:code',
		name: 'verify-email',
		component: VerifyEmailPage,
	},
	{
		path: '/info',
		name: 'info',
		component: InfoPage,
	},
	{
		path: '/settings',
		name: 'settings',
		component: SettingsPage,
	},
	{
		path: '/settings/:id',
		name: 'settings-type',
		component: SettingsPage,
	},
	{
		path: '/settings/:id/:subnetwork_id',
		name: 'settings-sub-network-edit',
		component: SettingsPage,
	},
	{
		path: '/license-alert',
		name: 'license-alert-page',
		component: LicenseAlertPage,
	},
	{
		path: '/cnet-constructor-add',
		name: 'cnet-constructor-add',
		component: EmptyPage,
	},
	{
		path: '/cnet-constructor',
		name: 'cnet-constructor',
		component: CNetListPage,
	},
	{
		path: '/cnet-constructor/:id',
		name: 'cnet-constructor-edit',
		component: EmptyPage,
	},
	{
		path: '/dashboard',
		name: 'dashboard',
		component: DashboardPage,
	},
	{
		path: '/insights',
		name: 'insights',
		component: InsightsPage,
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'not-found-page',
		component: NotFoundPage,
	},
]


const ALLOW_UNAUTHORIZED_ACCESS = [
	'home-map', 'home-public', 'login',
	'base-stations',
	'bs-on-map',
	'verify-email',
];

axios.defaults.baseURL = BASE_DOMAIN ? `//${BASE_DOMAIN}/api/v1/` : '/api/v1/';

const pinia = createPinia();

const router = createRouter({
	history: createWebHistory(),
	routes,
});
router.beforeEach(to => {
	const userStore = useUserStore();
	if (!userStore.isAuthenticated && !ALLOW_UNAUTHORIZED_ACCESS.includes(to.name)) {
		return { name: 'login' };
	}
});
router.afterEach((to, from) => {
	let route = '';
	if (from.name === 'base-stations' && ['base-stations-edit', 'add-base-station'].includes(to.name)) {
		route = 'base-stations';
	} else if (['bs-on-map', 'home-map'].includes(from.name) && ['base-stations-edit', 'add-base-station'].includes(to.name)) {
		route = 'home-map';
	} else if (['bs-on-map', 'home-map'].includes(from.name) && to.name === 'baselines-edit') {
		route = 'home-map';
	} else if (['baselines-edit', 'add-baseline'].includes(to.name)) {
		route = 'baselines';
	}
	useSessionStore().updateCloseButtonRoute(route);
});

app.use(router, axios);
app.use(i18n);
app.use(pinia);
router.isReady().then(() => {
	app.mount('#app');
	document.getElementById('app').classList.add('ready');
});

export { router }
